/* eslint-disable react-hooks/rules-of-hooks */
import { IPublishPage, StockInfo, ImageIdList, IDataBaseEditParam, IOCCAnnotationParam, IAnnotatioBodyParams, IPublishArticle, IReviewPageContent, IUpdateBodyParams, ActionType, MaterialListQueryParams, DraftListQueryParams } from '@/services/interface'
import createInstance, { getImgHost, getAnnotationUrlPrefix, occUserService, asyncTaskBaseURL } from '@/services/reqconfig/aiwriter'
import { QuestionDetail, QuestionItem } from '@/types/annotation'
import type { RequestConfig, SnsHttpParams } from '@thsf2e/useRequest/lib/interface'
import { ICharacterFilter, ISelectedCharacterFilter } from '@/types/aiWriting'
import { getLocale } from '@umijs/max'
import { isDev } from '@/services/utils'
import SparkMD5 from 'spark-md5'
const hostname = getImgHost()
const appId = '9rVEk3WTnO'
export const NOAUTH = -1010  // 文章校验无权限的code
export const INEXISTENCE = -1004

// MD5 哈希函数（用于生成 foreignKey）
// 使用 spark-md5 库
function md5(str: string): string {
  return SparkMD5.hash(str) // 返回 32 位 hex
}

// 生成 foreignKey: interactive_{md5_hash}
function buildForeignKey(prompt: string): string {
  const tsMs = Date.now() // 毫秒级时间戳
  const raw = `${tsMs}_${prompt}`
  const keyHash = md5(raw)
  return `interactive_${keyHash}`
}

const useRequest = (params: SnsHttpParams, config?: RequestConfig) => {
  const instance = createInstance()
  const localLang = getLocale()
  return instance({
    ...params,
    headers: {
      ...(params.headers || {}),
      language: localLang === 'zh-CN' ? 'zh' : 'en',
    }
  }, config)

}

/**
 * 获取ai检索
 * @param params
 * @returns
 */
export const getSearchAnswer = (params: { text: string }): {
  request: () => Promise<{
    answer: string
    reference: string[]
  }>;
  abort: AbortController
} => {
  const abort = new AbortController()
  return {
    request: () => useRequest({
      url: '/search/v1/search_answer',
      method: 'post',
      bodyParams: params,
      signal: abort.signal
    }),
    abort
  }
}

// 获取文章列表（支持状态筛选）
export const getDraftList = (params?: DraftListQueryParams) => {
  return useRequest({
    url: '/draft/v2/list',
    method: 'get',
    urlParams: params,
  })
}

export const getMaterialList = (params?: MaterialListQueryParams) => {
  return useRequest({
    url: '/material/v1/list',
    method: 'get',
    urlParams: params,
  })
}

export const getAuhtorList = (params?: {name: string}) => {
  return useRequest({
    url: '/author/v1/search',
    method: 'get',
    urlParams: params,
  })
}

export const getPublishAuthors = () => {
  return useRequest({
    url: '/author/v1/publish',
    method: 'get',
  })
}


// 删除文章
export const removeDraft = (params: Pick<IPublishPage, 'id'>) => {
  const url = `/draft/v1/delete/${params.id}`
  return useRequest({
    url,
    method: 'post',
  })
}

// 取消定时发布
export const cancelScheduled = (params: { id: string }) => {
  const url = `/draft/v1/publish/cancel/${params.id}`
  return useRequest({
    url,
    method: 'post',
  })
}

// 创建草稿
export const createDraft = (annotationId?: string) => {
  const params: any = {}
  if (annotationId) {
    params['annotationId'] = annotationId
  }

  return useRequest({
    url: '/draft/v1/create',
    method: 'post',
    bodyParams: params,
  })
}

// 获取所有发布渠道
export const getAllChannels = () => {
  return useRequest({
    url: '/draft/v1/publish/channels',
    method: 'get',
  })
}

// 发布文章
export const publishPage = (params: IPublishArticle) => {
  const { id } = params
  const url = `/draft/v1/publish/${id}`
  return useRequest({
    url,
    method: 'post',
    bodyParams: params,
  })
}

// 实时保存草稿
export const runtimeSavePage = (params: Pick<IPublishPage, 'id' | 'title' | 'content' | 'origContent' | 'prompt'>) => {
  const { id } = params
  const url = `/draft/v1/save/${id}`
  return useRequest({
    url,
    method: 'post',
    bodyParams: params,
  })
}

// 实时保存精编文章
export const runtimeSaveReviewPage = (params: Pick<IPublishPage, 'id' | 'title' | 'content' | 'origContent' | 'prompt'>, review?: boolean) => {
  if (review) {
    return updateReviewPage({
      action: ActionType.save,
      id: params.id,
      title: params.title,
      content: params.content,
    })
  }
  return runtimeSavePage(params)
}

// 读取草稿内容
export const getPageContent = (params: Pick<IPublishPage, 'id'>) => {
  const url = `/draft/v1/get/${params.id}`
  return useRequest({
    url,
    method: 'get',
  }, {
    dataFormat: (res: any) => {
      const { status_code, data } = res
      if (status_code === -1010) {
        // 文章无权限, 直接返回校验code
        return -1010
      }

      if (status_code === INEXISTENCE) {
        // 文章id不存在
        return INEXISTENCE
      }
      return data
    }
  })
}

// 上传图片
export const uploadImg = (file: FormData) => {
  return useRequest({
    // url: '/file/v1/upload',
    // url: 'https://occ.lighthorse.io/content-admin/open/api/file/v1/upload',
    url: '/file/v1/upload',
    method: 'post',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    bodyParams: file,
  })
}

// 搜索取数/获取知识库
export const getSearchData = (params: { text: string }, signal: AbortSignal) => {
  const url = `/search/v1/search_data`
  return useRequest({
    url,
    method: 'post',
    bodyParams: params,
    signal
  })
}

// 个股搜索
export const searchStock = (params: { keyword: string, language: 'en' | 'cn' }): Promise<StockInfo[]> => {
  return useRequest({
    url: '/stock/v1/search',
    method: 'get',
    urlParams: params
  })
}


// wordSmith聊天
export const getWordSmithChat = (params: { text: string }, callBack?: Function): Promise<StockInfo[]> => {
  return useRequest({
    url: '/wordsmith/v2/ask',
    method: 'post',
    bodyParams: params,
    timeout: 1000 * 180,   // 设置180s超时
  }, {
    plugins: {
      response: () => {
        if (!callBack) {
          return
        }
        const callBackRes = callBack()
        if (!callBackRes) {
          return
        }
      }
    }
  })
}

// 注册文生图任务
export const getImageId = (params: { text: string }): Promise<string> => {
  const foreignKey = buildForeignKey(params.text)
  const dataStr = JSON.stringify({ generateImagePrompt: params.text })
  
  return useRequest({
    // 直接使用 async-task 独立域名，避免叠加 /ai-writer/api
    url: `${asyncTaskBaseURL}/task/v1/register`,
    method: 'post',
    bodyParams: {
      code: 'QWEN_GENERATE_IMAGE_CARTOON_COLORED',
      data: dataStr,
      foreignKey: foreignKey,
    },
    timeout: 1000 * 10,   // 设置10s超时
  })
}

// 查询任务结果
export const getImageList = (params: { id: string }): Promise<{
  data: string; // JSON 字符串，成功时包含 FINAL_IMAGE_URL
  status: 'PENDING' | 'EXECUTING' | 'SUCCEED' | 'FAILED';
}> => {
  return useRequest({
    // 直接使用 async-task 独立域名，避免叠加 /ai-writer/api
    url: `${asyncTaskBaseURL}/task/v1/fetch/${params.id}`,
    method: 'get',
    timeout: 1000 * 10,   // 设置10s超时
  })
}


// 知识库编辑
export const setDadaBaseEdit = (params: IDataBaseEditParam): Promise<any> => {
  return useRequest({
    url: `/search/v1/data_edit`,
    method: 'post',
    bodyParams: params,
  })
}

// 获取问题列表
export const queryQuestionList = (params: { date: string }): Promise<{ annotations: QuestionItem[] }> => {
  return useRequest({
    url: `/annotation/v1/list?date=${params.date}`,
    method: 'get',
  })
}

// 获取问题详情
export const queryQuestionDetail = (params: { id: string }): Promise<QuestionDetail> => {
  return useRequest({
    url: `/annotation/v1/get/${params.id}`,
    method: 'get',
  })
}


// aime问答标注数据
export const annotationData = (id: string, bodyParams: IAnnotatioBodyParams): Promise<{
  data: QuestionDetail,
  status_code: number,
  status_msg: string
}> => {
  return useRequest({
    url: `/annotation/v1/submit/${id}`,
    method: 'post',
    bodyParams,
  }, {
    dataFormat: (res: any) => {
      return res
    }
  })
}


// 资讯标注平台数据标注
export const setOccDataAnnotation = (param: IOCCAnnotationParam) => {
  const apiPrefix = getAnnotationUrlPrefix()
  return useRequest({
    url: `${apiPrefix}/open/api/corpus/v1/add`,
    method: 'post',
    bodyParams: param,

  }, {
    dataFormat: (res: any) => {
      return res
    }
  })
}

// 通过prompt获取AIME的可视化图表数据
export const getAimeVisualData = (prompt: string): Promise<any> => {
  return useRequest({
    url: `/visual/generate`,
    // url: "https://yapi.myhexin.com/yapi/mock_v2/324518/ai-writer/api/visual/generate",
    method: 'post',
    bodyParams: {
      prompt
    }
  }, {
    dataFormat: (res: any) => {
      return res
    }
  })
}

// 获取AI推荐的人设
export const getAIRecommendCharacter = (text: string): Promise<any> => {
  return useRequest({
    url: `/character/v1/recommend`,
    method: 'get',
    urlParams: {
      text
    }
  })
}

// 认领素材（从审核进入编辑时锁定）
const claimingRequests = new Map<string, Promise<any>>()

export const claimMaterial = (params: { id: string }): Promise<any> => {
  const existingRequest = claimingRequests.get(params.id)
  if (existingRequest) {
    return existingRequest
  }

  const request = useRequest({
    url: `/material/v1/claim`,
    method: 'post',
    bodyParams: params,
  })

  claimingRequests.set(params.id, request)
  request.then(() => {
    if (claimingRequests.get(params.id) === request) {
      claimingRequests.delete(params.id)
    }
  }, () => {
    if (claimingRequests.get(params.id) === request) {
      claimingRequests.delete(params.id)
    }
  })
  return request
}


// 获取人设筛选项
export const getCharacterFilter = (): Promise<ICharacterFilter[]> => {
  return useRequest({
    url: `/character/v1/conditions`,
    method: 'get',
  })

}

// 获取人设列表
export const getCharacterList = (param: ISelectedCharacterFilter[]) => {
  return useRequest({
    url: `/character/v1/list`,
    method: 'post',
    bodyParams: {
      conditions: param
    }
  })
}

// occ的用户获取服务
export const getOccUserIfm = () => {
  return useRequest({
    url: `${occUserService}/v1/getCurrentUser`,
    method: 'get',
  })
}

// 获取精编文章详情内容
export const getReviewPageContent = (params: Pick<IReviewPageContent, 'id'>) => {
  const url = `/material/v1/detail/${params.id}`
  return useRequest({
    url,
    method: 'get',
  }, {
    dataFormat: (res: any) => {
      const { status_code, data } = res
      if (status_code === NOAUTH) {
        // 文章无权限, 直接返回校验code
        return NOAUTH
      }

      if (status_code === INEXISTENCE) {
        // 文章id不存在
        return INEXISTENCE
      }
      return data
    }
  })
}

export const updateReviewPage = (params: IUpdateBodyParams) => {
  return useRequest({
    url: '/material/v1/update',
    method: 'post',
    bodyParams: params,
  })
}

export const getDetailByUserIds = (params: { userIds: string[] }) => {
  const userIds = params.userIds.join(',')
  const hostname = isDev ? '//yapi.myhexin.com/yapi/mock_v2/310883' : '//api.ainvest.com'
  return useRequest({
    url: `${hostname}/social-center/account/getDetailByUserIds`,
    method: 'get',
    urlParams: { userIds },
  }, {
    dataFormat: (res: any) => {
      return res.data
    },
  })
}

// 评论相关操作

// 查询评论列表
export const getCommentList = (params: { materialId: string }) => {
  return useRequest({
    url: '/comment/v1/list',
    method: 'post',
    bodyParams: params,
  })
}

// 添加评论
export const addComment = (params: { materialId: string, commentContent: string, id: string }) => {
  return useRequest({
    url: '/comment/v1/create',
    method: 'post',
    bodyParams: params,
  })
}

// 更新评论
export const updateComment = (params: { commentContent: string, id: string }) => {
  return useRequest({
    url: '/comment/v1/update',
    method: 'post',
    bodyParams: params,
  })
}

// 删除评论
export const deleteComment = (params: { id: string }) => {
  return useRequest({
    url: `/comment/v1/delete/${params.id}`,
    method: 'post',
  })
}

// 检查是否有精编权限
export const checkReviewPermission = () => {
  return useRequest({
    url: '/material/v1/permission/check',
    method: 'get',
  })
}

// 提交预览评分
export interface SubmitPreviewScoreParams {
  newsId: string
  title: string
  content: string
}

export interface PreviewScoreResponse {
  news_id: string
  status: 'DONE' | 'PENDING'
  result: {
    total_score_100: number | null
    dimensions: Record<string, {
      score: number | null
      reason: string | null
      details: string | null
    } | null>
  } | null
  error_message?: string | null
}

export const submitArticlePreview = (params: SubmitPreviewScoreParams): Promise<PreviewScoreResponse> => {
  // Note: According to the docs, the API path is /api/scoreboard/articles/preview/submit
  // If the API is not under baseURL, you may need to use the full path
  // Please adjust the path according to the actual backend configuration
  return useRequest({
    url: '/evaluate/api/scoreboard/articles/preview/submit',
    method: 'post',
    bodyParams: params,
  }, {
    dataFormat: (res: any) => {
      // Handle response data, extract data field
      // Check for error response first
      if (res?.code && res.code !== 200) {
        const errorMsg = res.message || `Request failed with code ${res.code}`
        throw new Error(errorMsg)
      }
      // Check HTTP status code in response
      if (res?.status && res.status >= 400) {
        const errorMsg = res.statusText || `Request failed with status ${res.status}`
        throw new Error(errorMsg)
      }
      if (res?.data) {
        return res.data
      }
      return res
    },
    baseURL: '',
  }).then((result: any) => {
    return result
  }).catch((err: any) => {
    // Ensure errors are properly propagated
    // If error is already an Error object, rethrow it
    if (err instanceof Error) {
      throw err
    }
    // Otherwise, create a new Error from the error object
    const errorMessage = err?.message || err?.response?.data?.message || err?.response?.statusText || err?.response?.data || 'Failed to submit evaluation'
    throw new Error(errorMessage)
  })
}

// 查询预览评分结果
export const getArticlePreviewResult = (newsId: string): Promise<PreviewScoreResponse | null> => {
  return useRequest({
    url: `/evaluate/api/scoreboard/articles/preview/${newsId}`,
    method: 'get',
  }, {
    dataFormat: (res: any) => {
      // 处理响应数据
      // 如果返回 code 200 且 data 为 null，表示没有缓存
      if (res?.code === 200 && res?.data === null) {
        return null
      }
      // 如果有 data，返回 data
      if (res?.data) {
        return res.data
      }
      return res
    },
    baseURL: '',
  })
}
